import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, PlayCircle, RefreshCcw, FileText, Star, RotateCcw, Loader2, MapPinned, Paperclip, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, fmtDate, fmtDateTime, apiError, STATUS_LABELS, STATUS_STYLES, downloadUrl, streamUrl, invoiceUrl, coverSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StarRating } from "../components/StarRating";
import { TrackingTimeline } from "../components/TrackingTimeline";
import { Messenger } from "../components/messaging/Messenger";
import { LmsTranscript } from "../components/LmsTranscript";

export default function ClientSpace() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState(null); // {token, title}
  const [refundFor, setRefundFor] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundFile, setRefundFile] = useState(null);
  const [tracking, setTracking] = useState(null); // {orderId, data}
  const [recordings, setRecordings] = useState(null);

  useEffect(() => { api.get("/visio/my/recordings").then((r) => setRecordings(r.data)).catch(() => setRecordings({ mode: "simulé", sessions: [] })); }, []);

  const openTracking = async (orderId) => {
    setTracking({ orderId, data: null });
    try {
      const { data } = await api.get(`/orders/${orderId}/tracking`);
      setTracking({ orderId, data });
    } catch {
      toast.error("Suivi indisponible");
      setTracking(null);
    }
  };
  const [reviewFor, setReviewFor] = useState(null); // {product_id, title}
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [momoPay, setMomoPay] = useState(null); // {order_id, product_id, milestone_key, label, amount}
  const [momoOperator, setMomoOperator] = useState("Orange Money");
  const [momoPhone, setMomoPhone] = useState("");
  const [payingKey, setPayingKey] = useState("");

  const payMilestoneStripe = async (order_id, product_id, milestone_key) => {
    setPayingKey(`${order_id}-${milestone_key}`);
    try {
      const { data } = await api.post("/prestations/milestone/stripe", { order_id, product_id, milestone_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPayingKey(""); }
  };

  const submitMomoPay = async () => {
    if (!momoPay) return;
    if (!momoPhone.trim()) { toast.error("Numéro de téléphone requis"); return; }
    setBusy(true);
    try {
      await api.post("/prestations/milestone/momo", { order_id: momoPay.order_id, product_id: momoPay.product_id, milestone_key: momoPay.milestone_key, operator: momoOperator, phone: momoPhone });
      toast.success("Paiement confirmé — livrables débloqués.");
      setMomoPay(null); setMomoPhone("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, r, w] = await Promise.all([api.get("/orders/my"), api.get("/refunds/my"), api.get("/credits/wallet")]);
      setOrders(o.data);
      setRefunds(r.data);
      setWallet(w.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cs = params.get("credits_session");
    if (!cs) return;
    let tries = 0;
    const clear = () => { window.history.replaceState({}, "", window.location.pathname); };
    const poll = async () => {
      try {
        const { data } = await api.get(`/credits/purchase/status/${cs}`);
        if (data.payment_status === "paid") { toast.success("Offre activée — vos unités sont créditées."); clear(); load(); return; }
      } catch {}
      if (++tries < 10) setTimeout(poll, 2000); else { clear(); }
    };
    poll();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("milestone_session");
    if (!sid) return;
    let tries = 0;
    const clear = () => { window.history.replaceState({}, "", window.location.pathname); };
    const poll = async () => {
      try {
        const { data } = await api.get(`/prestations/milestone/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Paiement du jalon confirmé — livrables débloqués."); clear(); load(); return; }
      } catch {}
      if (++tries < 10) setTimeout(poll, 2000);
      else { clear(); toast.info("Paiement en cours de confirmation. Actualisez dans un instant."); }
    };
    poll();
  }, [load]);

  const renew = async (token) => {
    try {
      await api.post(`/downloads/${token}/renew`);
      toast.success("Lien renouvelé : 48 h et 3 téléchargements à nouveau disponibles.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitRefund = async () => {
    if (refundReason.trim().length < 10) { toast.error("Motif trop court (10 caractères minimum)"); return; }
    setBusy(true);
    try {
      let attachment = "";
      if (refundFile) {
        const fd = new FormData();
        fd.append("file", refundFile);
        const { data } = await api.post("/upload/refund-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment = data.path;
      }
      await api.post("/refunds", { order_id: refundFor, reason: refundReason, attachment });
      toast.success("Demande de remboursement envoyée.");
      setRefundFor(null); setRefundReason(""); setRefundFile(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const PKG_LABEL = { video_youtube: "Vidéo YouTube", video_drive: "Vidéo Google Drive", pdf: "PDF", excel: "Excel", url: "Lien" };

  const submitReview = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { product_id: reviewFor.product_id, rating, comment });
      toast.success("Merci pour votre évaluation !");
      setReviewFor(null); setComment(""); setRating(5);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openInvoice = async (orderId) => {
    try {
      const res = await api.get(`/orders/${orderId}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture-${orderId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Facture indisponible"); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-14" data-testid="client-space">
      <p className="font-mono text-xs uppercase tracking-widest text-ciel">Espace client</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy">Bonjour, {user?.name}</h1>

      {wallet && (() => {
        const total = wallet.units_total || 0;
        const remaining = wallet.units_remaining || 0;
        const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
        const depleted = wallet.expired || remaining <= 0;
        const low = !depleted && pct <= 25;
        const color = depleted ? "bg-red-500" : low ? "bg-amber-500" : "bg-emerald-500";
        const textColor = depleted ? "text-red-600" : low ? "text-amber-600" : "text-emerald-600";
        return (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="consumption-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-ciel">Mes unités de temps</p>
                <p className="mt-1 text-sm text-slate-500">Formule : <span className="font-semibold text-navy">{wallet.package_name || "Aucune"}</span></p>
              </div>
              <Link to="/offres" data-testid="dashboard-upgrade" className="rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy/90">
                {wallet.package_name ? "Upgrader mon offre" : "Acheter des unités"}
              </Link>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs text-slate-400">Consommées</p><p className="font-display text-2xl font-bold text-navy" data-testid="units-used">{wallet.units_used || 0}</p></div>
              <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs text-slate-400">Restantes</p><p className={`font-display text-2xl font-bold ${textColor}`} data-testid="units-remaining">{remaining}</p></div>
              <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs text-slate-400">{wallet.expired ? "Expiré le" : "Expire le"}</p><p className="font-display text-lg font-bold text-navy" data-testid="units-expiry">{wallet.expires_at ? new Date(wallet.expires_at).toLocaleDateString("fr-FR") : "—"}</p></div>
            </div>
            <div className="mt-4">
              <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} data-testid="units-progress" />
              </div>
              <p className={`mt-1.5 text-xs font-medium ${textColor}`} data-testid="units-status">
                {depleted ? "Crédits épuisés ou expirés — rechargez votre offre." : low ? `Niveau faible (${pct}%) — pensez à recharger.` : `${pct}% de vos unités disponibles.`}
              </p>
            </div>
          </div>
        );
      })()}

      <Tabs defaultValue="commandes" className="mt-10">
        <TabsList>
          <TabsTrigger value="commandes" data-testid="tab-orders">Mes commandes</TabsTrigger>
          <TabsTrigger value="formations" data-testid="tab-formations">Mes formations</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="remboursements" data-testid="tab-refunds">Remboursements</TabsTrigger>
          <TabsTrigger value="enregistrements" data-testid="tab-recordings">Enregistrements</TabsTrigger>
        </TabsList>

        <TabsContent value="formations" className="mt-6">
          <LmsTranscript />
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <Messenger mode="client" />
        </TabsContent>

        <TabsContent value="commandes" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-ciel" size={26} /></div>
          ) : orders.length === 0 ? (
            <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-orders">
              Aucune commande pour le moment.
            </p>
          ) : (
            <div className="space-y-5">
              {orders.map((o) => (
                <article key={o.order_id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`order-${o.order_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-sm font-semibold text-navy">{o.order_id}</p>
                      <p className="mt-0.5 text-xs text-slate-400">{fmtDate(o.created_at)} · {o.payment_method === "stripe" ? "Carte bancaire" : o.payment_method === "mobile_money" ? "Mobile Money" : "—"}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${STATUS_STYLES[o.status]}`} data-testid={`order-status-${o.order_id}`}>
                        {STATUS_LABELS[o.status]}
                      </span>
                      <span className="font-display text-base font-bold text-navy">{fmtXof(o.total)}</span>
                    </div>
                  </div>

                  <ul className="mt-5 space-y-3 border-t border-slate-100 pt-4">
                    {o.items.map((i) => (
                      <li key={i.product_id} className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{i.title}{i.qty > 1 && <span className="font-mono text-xs text-slate-400"> ×{i.qty}</span>}</p>
                          {i.kind === "download" && (
                            <p className="mt-0.5 font-mono text-[11px] text-slate-400" data-testid={`download-info-${i.product_id}`}>
                              {i.download_uses}/{i.download_max} téléchargements · expire le {fmtDate(i.download_expires_at)}
                            </p>
                          )}
                          {i.kind === "stream" && <p className="mt-0.5 font-mono text-[11px] text-ciel">Streaming illimité</p>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {i.kind === "download" && (
                            <>
                              <a href={downloadUrl(i.access_token)} data-testid={`download-btn-${i.product_id}`}
                                className="flex items-center gap-1.5 rounded-full bg-ciel px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-sky-700">
                                <Download size={13} /> Télécharger
                              </a>
                              <button onClick={() => renew(i.access_token)} data-testid={`renew-btn-${i.product_id}`}
                                className="flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-ciel hover:text-ciel">
                                <RefreshCcw size={13} /> Renouveler
                              </button>
                            </>
                          )}
                          {i.kind === "stream" && (
                            <button onClick={() => setVideo({ token: i.access_token, title: i.title, stream_url: i.stream_url || "" })} data-testid={`stream-btn-${i.product_id}`}
                              className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-navy-light">
                              <PlayCircle size={13} /> Regarder
                            </button>
                          )}
                          {(o.status === "payee" || o.status === "livree") && !i.reviewed && (
                            <button onClick={() => setReviewFor({ product_id: i.product_id, title: i.title })} data-testid={`review-btn-${i.product_id}`}
                              className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700 transition-colors duration-200 hover:bg-amber-100">
                              <Star size={13} /> Évaluer
                            </button>
                          )}
                          {i.reviewed && <span className="font-mono text-[11px] text-emerald-600">Évalué ✓</span>}
                        </div>
                        {(o.status === "payee" || o.status === "livree") && (i.package_contents || []).some((c) => c.url) && (
                          <ul className="w-full space-y-1.5 border-t border-slate-100 pt-3" data-testid={`package-contents-${i.product_id}`}>
                            <li className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Contenus inclus</li>
                            {i.package_contents.filter((c) => c.url).slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((c, k) => (
                              <li key={k} className="flex items-center gap-2">
                                <a href={coverSrc(c.url)} target="_blank" rel="noreferrer" data-testid={`package-link-${i.product_id}-${k}`}
                                  className="flex items-center gap-1.5 text-xs font-semibold text-ciel hover:underline">
                                  <ExternalLink size={12} /> {c.title || PKG_LABEL[c.type] || "Contenu"}
                                </a>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{PKG_LABEL[c.type] || ""}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {i.offer_type === "prestation" && (
                          <div className="w-full space-y-3 border-t border-slate-100 pt-3" data-testid={`prestation-${i.product_id}`}>
                            <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-700">Prestation — accès progressif par jalons</p>
                            {(i.milestones || []).length > 0 && (
                              <ul className="space-y-1.5">
                                {i.milestones.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((m, k) => (
                                  <li key={k} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2" data-testid={`prestation-milestone-${i.product_id}-${m.key}`}>
                                    <span className="flex items-center gap-2 text-xs text-slate-700">
                                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${m.paid ? "bg-emerald-600" : "bg-slate-300"}`}>{k + 1}</span>
                                      {m.label}
                                    </span>
                                    <span className="flex items-center gap-2">
                                      <span className="font-mono text-xs text-slate-500">{fmtXof(m.amount || 0)}</span>
                                      {m.paid ? (
                                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Payé</span>
                                      ) : (
                                        <span className="flex items-center gap-1.5">
                                          <button onClick={() => payMilestoneStripe(o.order_id, i.product_id, m.key)} disabled={payingKey === `${o.order_id}-${m.key}`}
                                            data-testid={`pay-milestone-stripe-${i.product_id}-${m.key}`}
                                            className="rounded-full bg-navy px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-navy/90 disabled:opacity-50">
                                            {payingKey === `${o.order_id}-${m.key}` ? "…" : "Payer par carte"}
                                          </button>
                                          <button onClick={() => { setMomoPay({ order_id: o.order_id, product_id: i.product_id, milestone_key: m.key, label: m.label, amount: m.amount }); }}
                                            data-testid={`pay-milestone-momo-${i.product_id}-${m.key}`}
                                            className="rounded-full border border-amber-400 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700 hover:bg-amber-100">
                                            Mobile Money
                                          </button>
                                        </span>
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <ul className="space-y-1.5">
                              {(i.package_contents || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((c, k) => (
                                <li key={k} className="flex items-center gap-2">
                                  {c.locked ? (
                                    <span className="flex items-center gap-1.5 text-xs text-slate-400" data-testid={`prestation-locked-${i.product_id}-${k}`}>
                                      <Lock size={12} /> {c.title || PKG_LABEL[c.type] || "Livrable"} <span className="text-[10px]">(verrouillé)</span>
                                    </span>
                                  ) : (
                                    <a href={coverSrc(c.url)} target="_blank" rel="noreferrer" data-testid={`prestation-link-${i.product_id}-${k}`}
                                      className="flex items-center gap-1.5 text-xs font-semibold text-ciel hover:underline">
                                      <ExternalLink size={12} /> {c.title || PKG_LABEL[c.type] || "Livrable"}
                                    </a>
                                  )}
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{PKG_LABEL[c.type] || ""}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                    <button onClick={() => openTracking(o.order_id)} data-testid={`tracking-btn-${o.order_id}`}
                      className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-navy-light">
                      <MapPinned size={13} /> Suivi
                    </button>
                    {(o.status === "payee" || o.status === "livree" || o.status === "remboursee") && (
                      <button onClick={() => openInvoice(o.order_id)} data-testid={`invoice-btn-${o.order_id}`}
                        className="flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-ciel hover:text-ciel">
                        <FileText size={13} /> Facture PDF
                      </button>
                    )}
                    {o.refund_allowed && (
                      <button onClick={() => setRefundFor(o.order_id)} data-testid={`refund-btn-${o.order_id}`}
                        className="flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-4 py-2 text-xs font-semibold text-red-600 transition-colors duration-200 hover:bg-red-100">
                        <RotateCcw size={13} /> Demander un remboursement
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="remboursements" className="mt-6">
          {refunds.length === 0 ? (
            <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-refunds">Aucune demande de remboursement.</p>
          ) : (
            <div className="space-y-4">
              {refunds.map((r) => (
                <div key={r.refund_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`refund-${r.refund_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-mono text-sm font-semibold text-navy">{r.order_id}</p>
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                      r.status === "approuvee" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : r.status === "rejetee" ? "border-red-300 bg-red-50 text-red-700"
                      : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                      {r.status === "approuvee" ? "Approuvée" : r.status === "rejetee" ? "Rejetée" : "En attente"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{r.reason}</p>
                  {r.attachment && (
                    <a href={coverSrc(r.attachment)} target="_blank" rel="noreferrer" data-testid={`refund-attachment-${r.refund_id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-ciel hover:underline">
                      <Paperclip size={12} /> Voir le justificatif joint
                    </a>
                  )}
                  {r.motif && <p className="mt-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Réponse du vendeur : {r.motif}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="enregistrements" className="mt-6">
          <div data-testid="recordings-panel">
            {!recordings ? (
              <div className="flex justify-center py-16"><Loader2 className="animate-spin text-ciel" size={26} /></div>
            ) : recordings.sessions.length === 0 ? (
              <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-recordings">
                Aucune session enregistrée pour le moment.{recordings.mode === "simulé" ? " Les enregistrements vidéo seront disponibles une fois le forfait d'enregistrement Daily.co activé." : ""}
              </p>
            ) : (
              <div className="space-y-3">
                {recordings.sessions.map((s) => (
                  <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`recording-${s.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-display text-sm font-semibold text-navy">{s.type_label}</p>
                        <p className="text-xs text-slate-500">{new Date(s.start).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-500">{s.recordings.length} enregistrement(s)</span>
                    </div>
                    {s.joinable && s.join_token && (
                      <a href={`/visio/${s.join_token}?auto=1`} target="_blank" rel="noopener noreferrer" data-testid={`recording-join-${s.id}`}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-400">
                        <PlayCircle size={14} /> Rejoindre maintenant
                      </a>
                    )}
                    {s.recordings.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {s.recordings.map((r) => (
                          <div key={r.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
                            <span className="text-navy">{r.duration ? `${Math.round(r.duration / 60)} min` : "Vidéo"}</span>
                            {r.share_url ? <a href={r.share_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-semibold text-ciel"><PlayCircle size={13} /> Regarder</a> : <span className="text-slate-400">Lien bientôt disponible</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* Lecteur vidéo streaming */}
      <Dialog open={!!video} onOpenChange={() => setVideo(null)}>
        <DialogContent className="max-w-3xl" data-testid="stream-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{video?.title}</DialogTitle></DialogHeader>
          {video && (
            video.stream_url ? (
              /youtube\.com|youtu\.be|vimeo\.com/.test(video.stream_url) ? (
                <iframe
                  data-testid="stream-player"
                  className="aspect-video w-full rounded-lg bg-black"
                  src={video.stream_url
                    .replace(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/, "https://www.youtube.com/embed/$1")
                    .replace(/vimeo\.com\/(\d+)/, "https://player.vimeo.com/video/$1")}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={video.title}
                />
              ) : (
                <video controls autoPlay playsInline className="w-full rounded-lg bg-black" data-testid="stream-player" src={video.stream_url} />
              )
            ) : (
              <video key={video.token} controls autoPlay playsInline className="w-full rounded-lg bg-black" data-testid="stream-player"
                src={streamUrl(video.token)} />
            )
          )}
          <p className="text-xs text-slate-400">Lecture en streaming illimitée — non téléchargeable.</p>
        </DialogContent>
      </Dialog>

      {/* Suivi de commande */}
      <Dialog open={!!tracking} onOpenChange={() => setTracking(null)}>
        <DialogContent data-testid="tracking-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Suivi — {tracking?.orderId}</DialogTitle></DialogHeader>
          {!tracking?.data ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-ciel" size={24} /></div>
          ) : (
            <TrackingTimeline events={tracking.data.events} />
          )}
        </DialogContent>
      </Dialog>

      {/* Demande de remboursement */}
      <Dialog open={!!momoPay} onOpenChange={() => { setMomoPay(null); setMomoPhone(""); }}>
        <DialogContent data-testid="momo-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Paiement Mobile Money</DialogTitle></DialogHeader>
          {momoPay && (
            <p className="text-sm text-slate-500">Jalon <span className="font-semibold">{momoPay.label}</span> — montant à régler : <span className="font-mono font-semibold text-navy">{fmtXof(momoPay.amount || 0)}</span></p>
          )}
          <div className="mt-3 space-y-2">
            <select value={momoOperator} onChange={(e) => setMomoOperator(e.target.value)} data-testid="momo-operator"
              className="w-full rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-ciel">
              <option>Orange Money</option>
              <option>MTN Mobile Money</option>
              <option>Moov Money</option>
              <option>Wave</option>
            </select>
            <input value={momoPhone} onChange={(e) => setMomoPhone(e.target.value)} placeholder="Numéro (ex : +225 07 00 00 00 00)" data-testid="momo-phone"
              className="w-full rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-ciel" />
          </div>
          <Button data-testid="momo-submit" onClick={submitMomoPay} disabled={busy} className="mt-2 w-full rounded-full bg-amber-500 text-white hover:bg-amber-600">
            {busy ? "Traitement…" : "Confirmer le paiement"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundFor} onOpenChange={() => { setRefundFor(null); setRefundFile(null); }}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Demande de remboursement</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Commande <span className="font-mono font-semibold">{refundFor}</span> — dans les 7 jours suivant l'achat.</p>
          <textarea
            data-testid="refund-reason"
            rows={4}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Expliquez le motif de votre demande (minimum 10 caractères)…"
            className="mt-3 w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel"
          />
          <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 p-3 text-xs text-slate-500 hover:border-ciel" data-testid="refund-attachment-upload">
            <Paperclip size={14} />
            {refundFile ? `Justificatif joint : ${refundFile.name}` : "Joindre un justificatif (reçu PDF ou image — optionnel)"}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
              onChange={(e) => setRefundFile(e.target.files[0] || null)} />
          </label>
          <Button data-testid="refund-submit" onClick={submitRefund} disabled={busy} className="mt-2 w-full rounded-full bg-red-500 text-white hover:bg-red-600">
            {busy ? "Envoi…" : "Envoyer la demande"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Évaluation */}
      <Dialog open={!!reviewFor} onOpenChange={() => setReviewFor(null)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Évaluer : {reviewFor?.title}</DialogTitle></DialogHeader>
          <div className="mt-2"><StarRating value={rating} size={26} interactive onChange={setRating} /></div>
          <textarea
            data-testid="review-dialog-comment"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Votre commentaire (visible publiquement)…"
            className="mt-4 w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel"
          />
          <Button data-testid="review-dialog-submit" onClick={submitReview} disabled={busy} className="mt-2 w-full rounded-full bg-navy text-white hover:bg-navy-light">
            {busy ? "Envoi…" : "Publier mon avis"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
